/**
 * Edit a contact.
 */

import { Form, redirect, useActionData, useLoaderData, useNavigation } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import { updateContactInput, type Contact, type Organization } from '@erp/contracts'
import { api, ApiError } from '../api'
import { invalid, issuesFrom, type FormResult } from '../forms'
import { ContactFields, readContactForm } from '../components/contact-form'
import { contactName } from '../format'
import { Button, PageHeader } from '../ui'

interface Data {
  contact: Contact
  organizations: Organization[]
}

export async function loader({ params }: LoaderFunctionArgs): Promise<Data> {
  const id = params.contactId
  if (!id) throw new Response('Missing contact id', { status: 400 })

  const [contact, page] = await Promise.all([
    api.contacts.get(id),
    api.organizations.list({ limit: 200, sort: 'name', direction: 'asc' }),
  ])
  return { contact, organizations: page.rows }
}

export async function action({
  request,
  params,
}: ActionFunctionArgs): Promise<FormResult | Response> {
  const id = params.contactId
  if (!id) throw new Response('Missing contact id', { status: 400 })

  const parsed = updateContactInput.safeParse(readContactForm(await request.formData()))
  if (!parsed.success) return invalid(issuesFrom(parsed.error))

  try {
    await api.contacts.update(id, parsed.data)
    return redirect(`/crm/contacts/${id}`)
  } catch (error) {
    if (error instanceof ApiError) return invalid(error.issues, error.message)
    throw error
  }
}

export default function EditContact() {
  const { contact, organizations } = useLoaderData() as Data
  const result = useActionData() as FormResult | undefined
  const navigation = useNavigation()
  const saving = navigation.state === 'submitting'

  return (
    <>
      <PageHeader title={`Edit ${contactName(contact)}`} />
      {result?.message ? <div className="ax-alert ax-alert-danger">{result.message}</div> : null}

      <Form method="post" className="ax-form">
        <ContactFields
          contact={contact}
          organizations={organizations}
          issues={result?.issues ?? {}}
        />
        <div className="ax-form-actions">
          <Button variant="solid" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <a className="ax-btn" href={`/crm/contacts/${contact.id}`}>
            Cancel
          </a>
        </div>
      </Form>
    </>
  )
}
